/**
 * FinalisationPayoutsPanel — T3-171
 *
 * The Finalisation Payout payments of a finalised contract, and the action to CONFIRM one as paid
 * (date + bank reference). A payout is created 'Pending' by finalisation (and by a later reserve
 * payout) and, before T3-171, nothing could ever move it on — so "paid" could not be recorded.
 *
 * The agreed schedule lines the finalisation settled read 'Settling' until their investor's payout
 * is confirmed here, then 'Paid'. The amount is not editable: it was fixed by the finalisation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, TextField, Button,
  CircularProgress, Alert,
} from '@mui/material';
import { format } from 'date-fns';
import { apiGet, apiPost } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import { usePermissions } from '../../contexts/PermissionContext';
import { MODULES } from '../../contexts/PermissionConstants';
import { useNotification } from '../../contexts/NotificationContext';

const FinalisationPayoutsPanel = ({ contractId, currencyCode, onConfirmed }) => {
  const { canUpdate } = usePermissions();
  const notify = useNotification();
  const canConfirm = canUpdate(MODULES.PAYMENTS);

  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({});        // payment_id -> { date, reference }
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet(`/contracts/${contractId}/finalisation-payouts`);
      setPayouts(result.data || []);
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to load the finalisation payouts');
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => { load(); }, [load]);

  const draftFor = (id) => drafts[id] || { date: format(new Date(), 'yyyy-MM-dd'), reference: '' };
  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...draftFor(id), ...patch } }));

  const confirm = async (p) => {
    const draft = draftFor(p.payment_id);
    setConfirming(p.payment_id);
    try {
      await apiPost(`/contracts/${contractId}/finalisation-payouts/${p.payment_id}/confirm`, {
        payment_date: draft.date,
        payment_reference: draft.reference || null,
      });
      notify.success(`Payout to ${p.investor_name || 'investor'} confirmed as paid`);
      await load();
      if (onConfirmed) onConfirmed();
    } catch (e) {
      notify.error(e.response?.data?.error?.message || e.message || 'Failed to confirm the payout');
    } finally {
      setConfirming(null);
    }
  };

  if (loading) return <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold">Payouts</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
        The agreed schedule lines this finalisation settled show as Settling until the investor&apos;s payout is confirmed as paid.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {!error && payouts.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No payout was made at finalisation (the funds were held in Rollover Suspense).
        </Typography>
      )}
      {payouts.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Investor</TableCell>
              <TableCell align="right">Amount ({currencyCode})</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment Date</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {payouts.map((p) => {
              const pending = p.payment_status === 'Pending';
              const draft = draftFor(p.payment_id);
              return (
                <TableRow key={p.payment_id}>
                  {/* T3-172: a payout with no investor is a CONTRACT-LEVEL payout from a finalisation made
                      before payouts were recorded per investor (T3-167) -- it covers every investor. */}
                  <TableCell>{p.investor_id == null ? 'All investors (single contract-level payout)' : (p.investor_name || '—')}</TableCell>
                  <TableCell align="right">{formatNumber(parseFloat(p.payment_amount) || 0, 2)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={pending ? 'Pending' : 'Paid'}
                      color={pending ? 'warning' : 'success'}
                      variant={pending ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell>
                    {pending && canConfirm ? (
                      <TextField
                        type="date"
                        size="small"
                        value={draft.date}
                        onChange={(e) => setDraft(p.payment_id, { date: e.target.value })}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    ) : (p.payment_date ? format(new Date(p.payment_date), 'dd MMM yyyy') : '—')}
                  </TableCell>
                  <TableCell>
                    {pending && canConfirm ? (
                      <TextField
                        size="small"
                        placeholder="Bank reference"
                        value={draft.reference}
                        onChange={(e) => setDraft(p.payment_id, { reference: e.target.value })}
                      />
                    ) : (p.payment_reference || '—')}
                  </TableCell>
                  <TableCell align="right">
                    {pending && canConfirm && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!draft.date || confirming === p.payment_id}
                        onClick={() => confirm(p)}
                      >
                        {confirming === p.payment_id ? 'Confirming…' : 'Confirm Paid'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default FinalisationPayoutsPanel;
